import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { navigateTo } from 'gatsby-link';
import { graphql } from 'gatsby';
import { navigate } from 'gatsby';

import Header from '../components/Header';
import TopMenu from '../components/TopMenu';
import Footer from '../components/Footer';
import SubMenu from '../components/SubMenu';
import RegionalOverviewsMap from '../components/RegionalOverviewsMap';
import CountryProfile from '../components/CountryProfile';
import CountryProfileList from '../components/CountryProfileList';
import IrrigationCountryList from '../components/IrrigationCountryList';
import ReportsAndBrochures from '../components/ReportsAndBrochures';
import BackLink from '../components/BackLink';

import { menuNodesSorter, getCountryCodeFromPath } from '../utils';

import styles from './styles.module.scss';

export default class PageTemplate extends Component {
  constructor() {
    super();

    this.state = {
      subPages: [],
    };
  }

  componentDidMount = () => {
    const { data: { allMarkdownRemark: { edges } } } = this.props;

    const subPages = edges
      .sort(menuNodesSorter)
      .map(({ node }) => {
        const { path, title } = node.frontmatter;

        // Exclude the section "index" file, to be rendered
        // in the body of the page, and any potential child pages.
        if (path.endsWith('/index') || path.split('/').length >= 4) {
          return null;
        }

        return {
          link: path.endsWith('/') ? path : `${path}/`,
          label: title,
          children: this.findPageChildren(path),
        };
      })
      .filter(Boolean);

    this.setState({
      subPages,
    });
  }

  onRegionMapClick = region => {
    navigateTo(`/countries-and-basins/regional-overviews/${region}/`);
  }

  findPageChildren(parentPath) {
    const { data: { allMarkdownRemark: { edges } } } = this.props;

    return edges.filter(({ node }) => {
      const nodeProps = node.frontmatter;

      // If the node path is shorter or equal to the parent
      // path it means it's not a children. Also, if it's a
      // 4th-level page, don't include it in the direct children list.
      const isChildPage = nodeProps.path.length <= parentPath.length;
      const isFourthLevel = nodeProps.path.split('/').length > 4;

      if (isChildPage || isFourthLevel) {
        return false;
      }

      if (nodeProps.path.startsWith(parentPath)) {
        return true;
      }

      return false;
    }).sort(menuNodesSorter);
  }

  renderBody = () => {
    const { data: { markdownRemark: { html, frontmatter } } } = this.props;
    const isPublications = frontmatter.path.startsWith('/resources/publications/');
    const isCountryProfile = frontmatter.path.startsWith('/countries-and-basins/country-profiles/');
    const isIrrigationMapPage = frontmatter.path.startsWith('/geospatial-information/global-maps-irrigated-areas/irrigation-by-country/country/');
    const isIrrigationMaps = frontmatter.path === '/geospatial-information/global-maps-irrigated-areas/irrigation-by-country';
    const isCountryProfiles = frontmatter.path === '/countries-and-basins/country-profiles';
    const isRegionalOverviews = frontmatter.path === '/countries-and-basins/regional-overviews';
    const countryCode = isCountryProfile && getCountryCodeFromPath(frontmatter.path);
    const publicationCategory = frontmatter.path.split('/').filter(Boolean)[2];
    
    try {
      switch (frontmatter.path) {
        case '/activities/projects':
          navigate('/activities/projects/wapor');
          break;
        case '/resources/publications':
          navigate('/resources/publications/reports/');
          break;
        case '/activities/prev-projects':
          navigate('/activities/prev-projects/cws-phase1');
          break;
        default:
          break;
      }
    } catch (e) {}

    return (
      <div style={{ position: 'relative' }}>
        {isIrrigationMapPage && (
          <BackLink to="/geospatial-information/global-maps-irrigated-areas/irrigation-by-country">« Go back to country list</BackLink>
        )}

        <div
          className={classnames(styles.content, {
            'country-profile': isCountryProfile,
            'irrigation-map': isIrrigationMapPage,
            'river-basin': frontmatter.path.startsWith('/countries-and-basins/transboundary-river-basins'),
          })}
          dangerouslySetInnerHTML={{ __html: html }}
        />

        {isCountryProfile && <CountryProfile countryCode={countryCode} {...frontmatter} />}
        {isCountryProfiles && <CountryProfileList />}
        {isIrrigationMaps && <IrrigationCountryList listTitle="Sub-national statistics" />}

        {isPublications && (
          <div className={styles.publicationsWrapper}>
            <h2 style={{ textTransform: 'capitalize' }}>{publicationCategory}</h2>
            <ReportsAndBrochures category={publicationCategory} />
          </div>
        )}

        {isRegionalOverviews && (
          <div style={{ width: '100%', textAlign: 'center' }}>
            <RegionalOverviewsMap onRegionClick={this.onRegionMapClick} />
          </div>
        )}
      </div>
    );
  }

  render() {
    const { data: { site, markdownRemark }, location } = this.props;
    const { subPages } = this.state;
    const pageClass = markdownRemark.frontmatter.title
                      ? (markdownRemark.frontmatter.englishTitle
                          ? markdownRemark.frontmatter.englishTitle.toLowerCase().replace(/\s/gi, '-') // poor's man kebab-case)
                          : markdownRemark.frontmatter.title.toLowerCase().replace(/\s/gi, '-') // poor's man kebab-case)
                      ) : null;

    let pageClassFromPath = '';
    if (markdownRemark.frontmatter.path) {
      const fullPath = markdownRemark.frontmatter.path.split('/');
      const fullPathLen = fullPath.length;
      pageClassFromPath = fullPath[fullPathLen - 1].toLowerCase().replace(/\s/gi, '-') // poor's man kebab-case
    }

    const pageClasses = classnames({
      [pageClass]: true,
      [pageClassFromPath]: true
    });

    return (
      <div className={pageClasses}>
        <Header siteTitle={site.siteMetadata.title} location={{}} />

        <div className="app-wrapper">
          <h2 className="site-title">AQUASTAT - 粮农组织全球水与农业信息系统</h2>
          <TopMenu location={location.pathname} />
          {(markdownRemark.frontmatter.bannerUrl !== null && markdownRemark.frontmatter.bannerUrl !== '') &&
            (
              <div className={styles.headerImage}>
                <img src={markdownRemark.frontmatter.bannerUrl} alt={markdownRemark.frontmatter.title} />
              </div>
            )
          }
          <div className={styles.wrapper}>
            <div className={styles.subMenuWrapper}>
              <SubMenu links={subPages} currentPath={location.pathname} />
            </div>

            {this.renderBody()}
          </div>
        </div>

        <Footer />
      </div>
    );
  }
}

PageTemplate.propTypes = {
  data: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
};

/* eslint-disable no-undef */
export const pageQuery = graphql`
  query PageByPath($path: String!, $categoryRegexp: String!) {
    site {
      siteMetadata {
        title
      }
    }
    markdownRemark(frontmatter: { path: { eq: $path } }) {
      html
      frontmatter {
        path
        title
        englishTitle
        doNotPublishMap
        pdfLink
        year
        lang
        bannerUrl
      }
    }
    allMarkdownRemark(filter: { frontmatter: { path: { regex: $categoryRegexp }}}) {
      edges {
        node {
          id
          html
          frontmatter {
            path
            title
            menuOrder
          }
        }
      }
    }
  }
`;
